import { useMemo, useState } from 'react';
import { useFinancialSummary } from '../hooks/useFinancialSummary';
import { formatCurrency } from '../utils/format';
import {
  Alert,
  Box,
  CircularProgress,
  Chip,
  MenuItem,
  Select,
  Tooltip as MuiTooltip,
  Typography,
} from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PieChartOutlineIcon from '@mui/icons-material/PieChartOutline';

const OPEX_COLOR = '#ff9800';
const CAPEX_COLOR = '#42a5f5';

const MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

function fmt(val: number): string {
  if (Math.abs(val) >= 1_000_000) return `GH₵${(val / 1_000_000).toFixed(2)}M`;
  if (Math.abs(val) >= 1_000) return `GH₵${(val / 1_000).toFixed(1)}k`;
  return `GH₵${val.toFixed(0)}`;
}

function KpiCard({
  label,
  value,
  sub,
  color,
  icon,
}: {
  label: string;
  value: string;
  sub?: string;
  color: string;
  icon: React.ReactNode;
}) {
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 150,
        p: 2.5,
        borderRadius: 2.5,
        bgcolor: '#1a1a1a',
        border: `1px solid ${color}22`,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 0.75,
        }}
      >
        <Typography
          sx={{
            fontSize: '0.65rem',
            fontWeight: 700,
            color: '#555',
            letterSpacing: '0.8px',
            textTransform: 'uppercase',
          }}
        >
          {label}
        </Typography>
        <Box sx={{ color, opacity: 0.6, display: 'flex' }}>{icon}</Box>
      </Box>
      <Typography
        sx={{
          fontSize: '1.3rem',
          fontWeight: 800,
          color,
          fontVariantNumeric: 'tabular-nums',
          lineHeight: 1.2,
        }}
      >
        {value}
      </Typography>
      {sub && <Typography sx={{ fontSize: '0.68rem', color: '#555', mt: 0.35 }}>{sub}</Typography>}
    </Box>
  );
}

function ChartTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  const opex = payload.find((p: any) => p.dataKey === 'opex');
  const capex = payload.find((p: any) => p.dataKey === 'capex');
  const total = (opex?.value ?? 0) + (capex?.value ?? 0);
  return (
    <Box
      sx={{
        p: 1.5,
        bgcolor: '#1e1e1e',
        border: '1px solid #333',
        borderRadius: 1.5,
        minWidth: 160,
      }}
    >
      <Typography sx={{ fontSize: '0.72rem', color: '#888', mb: 0.75 }}>{label}</Typography>
      {opex && (
        <Typography sx={{ fontSize: '0.8rem', color: OPEX_COLOR }}>
          Opex: {fmt(opex.value)}
        </Typography>
      )}
      {capex && capex.value > 0 && (
        <Typography sx={{ fontSize: '0.8rem', color: CAPEX_COLOR }}>
          Capex: {fmt(capex.value)}
        </Typography>
      )}
      {total > 0 && (
        <Typography
          sx={{
            fontSize: '0.8rem',
            color: '#e0e0e0',
            fontWeight: 700,
            mt: 0.5,
            borderTop: '1px solid #333',
            pt: 0.5,
          }}
        >
          Total: {fmt(total)}
        </Typography>
      )}
    </Box>
  );
}

export default function FinancialSummary() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);

  const { data, isLoading, isError } = useFinancialSummary(year);

  const months = useMemo(() => (data?.months ?? []) as any[], [data]);
  const summary = useMemo(() => data?.summary ?? null, [data]);
  const availYears = useMemo(
    () => (data?.available_years ?? [currentYear]) as number[],
    [data, currentYear]
  );

  const chartData = useMemo(
    () =>
      months.map((m: any, i: number) => ({
        month: MONTH_LABELS[i],
        opex: m.opex,
        capex: m.capex,
        total: m.total,
        capex_breakdown: m.capex_breakdown ?? [],
      })),
    [months]
  );

  const peakMonth = useMemo(() => {
    if (!chartData.length) return null;
    return chartData.reduce((best, m) => (m.total > best.total ? m : best), chartData[0]);
  }, [chartData]);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#121212', p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
          flexWrap: 'wrap',
          gap: 1.5,
        }}
      >
        <Box>
          <Typography
            sx={{ fontWeight: 800, fontSize: { xs: '1.2rem', md: '1.5rem' }, color: '#f5f5f5' }}
          >
            Financial Summary
          </Typography>
          <Typography sx={{ fontSize: '0.78rem', color: '#555', mt: 0.25 }}>
            Actual cash spent — operational costs and capital investments combined
          </Typography>
        </Box>
        <Select
          size="small"
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          sx={{
            bgcolor: '#1a1a1a',
            color: '#e0e0e0',
            borderRadius: 1.5,
            minWidth: 90,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.1)' },
            '& .MuiSelect-icon': { color: '#555' },
          }}
        >
          {availYears.map((y) => (
            <MenuItem key={y} value={y}>
              {y}
            </MenuItem>
          ))}
        </Select>
      </Box>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
          <CircularProgress sx={{ color: '#42a5f5' }} />
        </Box>
      )}

      {isError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load financial summary.
        </Alert>
      )}

      {!isLoading && !isError && summary && (
        <>
          {/* KPI strip */}
          <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
            <KpiCard
              label="Total Cash Out"
              value={fmt(summary.total_out)}
              sub={`${year} full year`}
              color="#e0e0e0"
              icon={<TrendingUpIcon fontSize="small" />}
            />
            <KpiCard
              label="Operational"
              value={fmt(summary.total_opex)}
              sub={`${summary.total_out > 0 ? ((summary.total_opex / summary.total_out) * 100).toFixed(0) : 0}% of total`}
              color={OPEX_COLOR}
              icon={<ReceiptLongIcon fontSize="small" />}
            />
            <KpiCard
              label="Capital"
              value={fmt(summary.total_capex)}
              sub={`${summary.capex_share}% of total`}
              color={CAPEX_COLOR}
              icon={<AccountBalanceIcon fontSize="small" />}
            />
            {peakMonth && peakMonth.total > 0 && (
              <KpiCard
                label="Peak Month"
                value={peakMonth.month}
                sub={fmt(peakMonth.total)}
                color="#ab47bc"
                icon={<PieChartOutlineIcon fontSize="small" />}
              />
            )}
          </Box>

          {/* Legend chips */}
          <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
            <Chip
              size="small"
              label="Operational (Opex)"
              sx={{
                bgcolor: `${OPEX_COLOR}18`,
                color: OPEX_COLOR,
                border: `1px solid ${OPEX_COLOR}30`,
                fontSize: '0.72rem',
                fontWeight: 700,
              }}
            />
            <Chip
              size="small"
              label="Capital (Capex)"
              sx={{
                bgcolor: `${CAPEX_COLOR}18`,
                color: CAPEX_COLOR,
                border: `1px solid ${CAPEX_COLOR}30`,
                fontSize: '0.72rem',
                fontWeight: 700,
              }}
            />
          </Box>

          {/* Stacked bar chart */}
          <Box
            sx={{
              bgcolor: '#1a1a1a',
              borderRadius: 2.5,
              border: '1px solid rgba(255,255,255,0.07)',
              p: 2.5,
              mb: 3,
            }}
          >
            <Typography
              sx={{
                fontSize: '0.7rem',
                fontWeight: 700,
                color: '#555',
                letterSpacing: '0.7px',
                textTransform: 'uppercase',
                mb: 2,
              }}
            >
              Monthly Cash Out — {year}
            </Typography>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart
                data={chartData}
                barSize={28}
                margin={{ top: 4, right: 8, left: 0, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="rgba(255,255,255,0.05)"
                  vertical={false}
                />
                <XAxis
                  dataKey="month"
                  tick={{ fill: '#666', fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tickFormatter={fmt}
                  tick={{ fill: '#666', fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  width={64}
                />
                <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(255,255,255,0.03)' }} />
                <Bar
                  dataKey="opex"
                  stackId="a"
                  fill={OPEX_COLOR}
                  opacity={0.85}
                  radius={[0, 0, 0, 0]}
                  name="Opex"
                />
                <Bar
                  dataKey="capex"
                  stackId="a"
                  fill={CAPEX_COLOR}
                  opacity={0.85}
                  radius={[3, 3, 0, 0]}
                  name="Capex"
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>

          {/* Monthly breakdown table */}
          <Box
            sx={{
              bgcolor: '#1a1a1a',
              borderRadius: 2.5,
              border: '1px solid rgba(255,255,255,0.07)',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ px: 2.5, py: 1.75, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <Typography
                sx={{
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  color: '#555',
                  letterSpacing: '0.7px',
                  textTransform: 'uppercase',
                }}
              >
                Monthly Breakdown
              </Typography>
            </Box>

            {/* Table header */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr 1.4fr 80px',
                px: 2.5,
                py: 1,
                borderBottom: '1px solid rgba(255,255,255,0.05)',
              }}
            >
              {['Month', 'Operational', 'Capital', 'Capital Investment', 'Total'].map((h) => (
                <Typography
                  key={h}
                  sx={{
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    color: '#444',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                  }}
                >
                  {h}
                </Typography>
              ))}
            </Box>

            {/* Rows */}
            {chartData.map((m, i) => {
              const isEmpty = m.total === 0;
              return (
                <Box
                  key={m.month}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr 1.4fr 80px',
                    px: 2.5,
                    py: 1.25,
                    borderBottom: i < 11 ? '1px solid rgba(255,255,255,0.04)' : 'none',
                    opacity: isEmpty ? 0.35 : 1,
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' },
                    alignItems: 'center',
                  }}
                >
                  <Typography sx={{ fontSize: '0.82rem', color: '#bbb', fontWeight: 600 }}>
                    {m.month}
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    {m.opex > 0 && (
                      <Box
                        sx={{
                          width: 3,
                          height: 14,
                          borderRadius: 1,
                          bgcolor: OPEX_COLOR,
                          flexShrink: 0,
                        }}
                      />
                    )}
                    <Typography
                      sx={{
                        fontSize: '0.82rem',
                        color: m.opex > 0 ? '#e0e0e0' : '#333',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {m.opex > 0 ? fmt(m.opex) : '—'}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    {m.capex > 0 && (
                      <Box
                        sx={{
                          width: 3,
                          height: 14,
                          borderRadius: 1,
                          bgcolor: CAPEX_COLOR,
                          flexShrink: 0,
                        }}
                      />
                    )}
                    <Typography
                      sx={{
                        fontSize: '0.82rem',
                        color: m.capex > 0 ? '#e0e0e0' : '#333',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {m.capex > 0 ? fmt(m.capex) : '—'}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(m.capex_breakdown as any[]).length === 0 ? (
                      <Typography sx={{ fontSize: '0.72rem', color: '#333' }}>—</Typography>
                    ) : (
                      (m.capex_breakdown as any[]).map((b: any) => (
                        <MuiTooltip
                          key={b.investment}
                          title={`${b.investment}: ${formatCurrency(b.amount)}`}
                        >
                          <Chip
                            label={b.investment}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: '0.62rem',
                              bgcolor: 'rgba(66,165,245,0.1)',
                              color: '#42a5f5',
                              border: '1px solid rgba(66,165,245,0.2)',
                              maxWidth: 120,
                              '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
                            }}
                          />
                        </MuiTooltip>
                      ))
                    )}
                  </Box>

                  <Typography
                    sx={{
                      fontSize: '0.84rem',
                      fontWeight: 700,
                      color: m.total > 0 ? '#e0e0e0' : '#333',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {m.total > 0 ? fmt(m.total) : '—'}
                  </Typography>
                </Box>
              );
            })}

            {/* Year total row */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr 1.4fr 80px',
                px: 2.5,
                py: 1.5,
                bgcolor: 'rgba(255,255,255,0.03)',
                borderTop: '1px solid rgba(255,255,255,0.09)',
              }}
            >
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#888' }}>
                Year Total
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.84rem',
                  fontWeight: 800,
                  color: OPEX_COLOR,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {fmt(summary.total_opex)}
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.84rem',
                  fontWeight: 800,
                  color: CAPEX_COLOR,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {summary.total_capex > 0 ? fmt(summary.total_capex) : '—'}
              </Typography>
              <Box />
              <Typography
                sx={{
                  fontSize: '0.84rem',
                  fontWeight: 800,
                  color: '#e0e0e0',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {fmt(summary.total_out)}
              </Typography>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
}
